import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Mail,
  CheckCircle,
  Send,
  ArrowLeft,
  Key,
  Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthContext } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';

/**
 * Email Verification Page - World-Class UI Implementation
 * 
 * Features:
 * - 2-column layout (form + authentication flow)
 * - SALONHUB logo integration
 * - Material-UI Design System v2.0
 * - Responsive design (mobile: 1-column, desktop: 2-column)
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 * 
 * @returns Email verification page component
 */
const EmailVerificationPage: React.FC = () => {
  usePageTitle('メール認証');
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { verifyEmail, resendVerificationEmail, loading, error, clearError } = useAuthContext();

  // フォーム状態
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  // 再送信関連
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // エラー状態
  const [localError, setLocalError] = useState('');

  // 60秒クールダウンタイマー
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // userIdが存在しない場合はログイン画面にリダイレクト
  useEffect(() => {
    if (!userId) {
      logger.error('❌ [EmailVerification] userIdが見つかりません');
      navigate('/login');
    }
  }, [userId, navigate]);

  /**
   * 認証コード検証
   * マルチテナント対応: 組織作成画面への分岐ロジック強化
   */
  const handleVerifyCode = useCallback(async () => {
    if (!userId) return;

    // バリデーション強化
    if (!verificationCode.trim()) {
      setLocalError('認証コードを入力してください');
      return;
    }

    if (verificationCode.length !== 6) {
      setLocalError('認証コードは6桁で入力してください');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setLocalError('認証コードは数字のみで入力してください');
      return;
    }

    setIsVerifying(true);
    setLocalError('');
    clearError();

    try {
      logger.log('🔐 [EmailVerification] 認証開始:', { userId, timestamp: new Date().toISOString() });

      const response = await verifyEmail({
        userId,
        verificationCode: verificationCode.trim()
      });

      if (response.success) {
        setVerificationSuccess(true);
        logger.log('✅ [EmailVerification] 認証成功:', { 
          userId, 
          shouldRedirectToOrganizationCreation: response.shouldRedirectToOrganizationCreation,
          timestamp: new Date().toISOString()
        });

        // 3秒後に適切な画面へ遷移（マルチテナント対応）
        setTimeout(() => {
          if (response.shouldRedirectToOrganizationCreation) {
            logger.log('🏢 [EmailVerification] 組織作成画面へリダイレクト');
            navigate('/organization-creation');
          } else {
            logger.log('🏠 [EmailVerification] ダッシュボードへリダイレクト');
            navigate('/dashboard');
          }
        }, 3000);
      }
    } catch (error) {
      logger.error('❌ [EmailVerification] 認証エラー:', { 
        error, 
        userId, 
        code: verificationCode,
        timestamp: new Date().toISOString()
      });

      if (error instanceof Error) {
        // エラーメッセージの改善
        const errorMessage = error.message;
        if (errorMessage.includes('expired') || errorMessage.includes('期限')) {
          setLocalError('認証コードの有効期限が切れています。新しいコードを再送信してください。');
        } else if (errorMessage.includes('invalid') || errorMessage.includes('無効')) {
          setLocalError('認証コードが正しくありません。メールを確認して正しいコードを入力してください。');
        } else if (errorMessage.includes('max_attempts') || errorMessage.includes('試行回数')) {
          setLocalError('認証の試行回数が上限に達しました。しばらく時間をおいてから再度お試しください。');
        } else {
          setLocalError(errorMessage);
        }
      } else {
        setLocalError('認証に失敗しました。認証コードを確認してください。');
      }
    } finally {
      setIsVerifying(false);
    }
  }, [userId, verificationCode, verifyEmail, clearError, navigate]);

  /**
   * 認証メール再送信
   * エラーハンドリング強化とレート制限対応
   */
  const handleResendCode = useCallback(async () => {
    if (!userId || resendCooldown > 0) return;

    setResendLoading(true);
    setLocalError('');
    clearError();

    try {
      logger.log('📧 [EmailVerification] 再送信開始:', { userId, timestamp: new Date().toISOString() });

      await resendVerificationEmail(userId);

      // 60秒クールダウン開始
      setResendCooldown(60);

      logger.log('✅ [EmailVerification] 再送信完了:', { userId, timestamp: new Date().toISOString() });
    } catch (error) {
      logger.error('❌ [EmailVerification] 再送信エラー:', { 
        error, 
        userId, 
        timestamp: new Date().toISOString()
      });

      if (error instanceof Error) {
        const errorMessage = error.message;
        if (errorMessage.includes('rate_limit') || errorMessage.includes('制限')) {
          setLocalError('再送信の制限に達しています。しばらく時間をおいてから再度お試しください。');
        } else if (errorMessage.includes('not_found') || errorMessage.includes('見つかり')) {
          setLocalError('ユーザーが見つかりません。ログイン画面からやり直してください。');
        } else {
          setLocalError(errorMessage);
        }
      } else {
        setLocalError('認証メールの再送信に失敗しました。');
      }
    } finally {
      setResendLoading(false);
    }
  }, [userId, resendCooldown, resendVerificationEmail, clearError]);

  /**
   * ログイン画面に戻る
   */
  const handleBackToLogin = useCallback(() => {
    logger.log('🔙 [EmailVerification] ログイン画面へ戻る:', { userId });
    navigate('/login');
  }, [navigate, userId]);

  /**
   * Enterキー押下時の処理
   */
  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isVerifying && verificationCode.length === 6) {
      handleVerifyCode();
    }
  }, [handleVerifyCode, isVerifying, verificationCode]);

  // 表示するエラーを決定
  const displayError = localError || error;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      {/* LEFT COLUMN: Verification Form */}
      <div className="flex flex-col justify-center p-4 md:p-8 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="max-w-[480px] mx-auto w-full">
            {/* 認証成功画面 */}
            {verificationSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle className="w-20 h-20 text-green-500" />

                  <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 text-center">
                    認証完了！
                  </h1>

                  <p className="text-base text-gray-600 leading-relaxed text-center">
                    メール認証が正常に完了しました。
                    <br />
                    SALON HUBの世界へようこそ！
                    <br />
                    設定画面に移動しています...
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    <Loader2 className="h-10 w-10 text-green-500 animate-spin" />
                    <p className="text-sm text-gray-600">リダイレクト中...</p>
                  </div>
                </div>
              </motion.div>
            ) : (
              /* 認証コード入力画面 */
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* SALONHUB Logo */}
                <img
                  src="/assets/logos/salon-hub-logo.png"
                  alt="SALON HUB"
                  className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 hover:scale-105"
                />

                {/* Title */}
                <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-2 leading-tight">
                  メール認証
                </h1>

                <p className="text-base text-gray-600 leading-relaxed mb-1">
                  メールに送信された6桁の認証コードを入力してください
                </p>

                <p className="text-sm text-gray-500 mb-6">
                  サロン向け統合管理システム「SALON HUB」へようこそ
                </p>

                {displayError && (
                  <Alert className="mb-4 border-red-200 bg-red-50">
                    <AlertDescription className="text-sm text-red-800">
                      {displayError}
                    </AlertDescription>
                  </Alert>
                )}

                {/* 認証コード入力フィールド */}
                <div className="space-y-2 mb-2">
                  <Label htmlFor="verificationCode">認証コード</Label>
                  <Input
                    id="verificationCode"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                      setVerificationCode(value);
                      if (localError || error) {
                        setLocalError('');
                        clearError();
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="例：123456"
                    maxLength={6}
                    disabled={isVerifying || loading}
                    autoFocus
                    className="text-center text-2xl font-semibold tracking-[0.5rem] bg-white"
                  />
                </div>

                <p className="text-xs text-center text-gray-500 mb-4">
                  数字6桁を入力してください（有効期限：10分）
                </p>

                {/* 認証ボタン */}
                <Button
                  onClick={handleVerifyCode}
                  disabled={isVerifying || loading || verificationCode.length !== 6}
                  className="w-full min-h-[56px] py-2.5 mb-4"
                >
                  {isVerifying || loading ? (
                    <div className="flex items-center gap-1">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>認証中...</span>
                    </div>
                  ) : (
                    '認証する'
                  )}
                </Button>

                <div className="relative mb-4">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-50 px-2 text-sm text-gray-500">
                    または
                  </span>
                </div>

                {/* 再送信セクション */}
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    認証コードが届かない場合
                  </p>

                  <Button
                    variant="outline"
                    onClick={handleResendCode}
                    disabled={resendLoading || resendCooldown > 0}
                    className="min-w-[200px] mb-2"
                  >
                    {resendLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {resendCooldown > 0
                      ? `再送信可能まで ${resendCooldown}秒`
                      : '認証メールを再送信'
                    }
                  </Button>

                  <p className="text-xs text-gray-500">
                    メールが届かない場合は、迷惑メールフォルダもご確認ください
                  </p>
                </div>

                <Separator className="mb-4" />

                {/* ログイン画面に戻るリンク */}
                <button
                  onClick={handleBackToLogin}
                  className="flex items-center justify-center gap-1 w-full text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  ログイン画面に戻る
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* RIGHT COLUMN: Visual Content */}
      <div className="hidden md:flex flex-col justify-center items-center p-8 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white relative overflow-hidden">
        <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] animate-[pulse_8s_ease-in-out_infinite]" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="z-10 max-w-[500px] w-full"
        >
          {/* Welcome Message */}
          <h2 className="scroll-m-20 text-3xl md:text-4xl font-bold tracking-tight mb-2 text-center leading-[1.3] [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
            あと少しで完了です
          </h2>

          <p className="text-center opacity-95 text-lg leading-relaxed mb-4">
            メール確認でSALON HUBを開始
          </p>

          {/* Divider */}
          <Separator className="my-6 bg-white/30" />

          {/* 認証フロー3ステップ */}
          <div className="mt-6">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="mb-6">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Mail className="w-7 h-7" />
                  1. メールを確認
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  受信トレイに届いた6桁の認証コードをチェック
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="mb-6">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Key className="w-7 h-7" />
                  2. コードを入力
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  左側の入力フィールドに6桁のコードを入力
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div>
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <CheckCircle className="w-7 h-7" />
                  3. 認証完了
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  SALON HUBの全機能がご利用いただけます
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;